import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Package,
  TrendingDown,
  Calendar,
  AlertTriangle,
  Skull,
  RefreshCw,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import catalog from "@/api/catalog";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/catalog/ProductCard";
import BulkPhotoUploadMode from "@/components/catalog/BulkPhotoUploadMode";
import CollapsibleFiltersPanel from "@/components/CollapsibleFiltersPanel";
import LiquidationDialog from "@/components/catalog/LiquidationDialog";
import { Link as RouterLink } from "react-router-dom";
import { Boxes } from "lucide-react";

const VIEWS = ["all", "ic338", "old", "no_sales", "dead"];
const VIEW_ICONS = {
  all: Package,
  ic338: Package,
  old: Calendar,
  no_sales: AlertTriangle,
  dead: Skull,
};

const SORTS = [
  "capital_tied_up",
  "days_in_stock",
  "days_since_last_transaction",
  "margin_pct",
  "qty_in_stock",
  "retail_price",
  "total_transactions",
];

function fmtMoney(n, opts = {}) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: opts.decimals ?? 0,
  });
}

export default function Catalog() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("catalog:manage");
  const [sp, setSp] = useSearchParams();

  const [view, setView] = useState(sp.get("view") || "all");
  const [search, setSearch] = useState(sp.get("search") || "");
  const [vendor, setVendor] = useState(sp.get("vendor") || "");
  const [productType, setProductType] = useState(sp.get("type") || "");
  const [sortBy, setSortBy] = useState(sp.get("sort") || "capital_tied_up");
  const [sortDir, setSortDir] = useState(sp.get("dir") || "desc");
  const [excludeAsIs, setExcludeAsIs] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(() => {
    // Phase 5.6 — initial value can come from `?photos=missing` (from the Dashboard widget).
    const v = sp.get("photos");
    if (v === "missing") return false;
    if (v === "yes") return true;
    return null;
  });
  // Phase 5.7 — Bulk-photo mode (toggled by the CTA banner).
  const [bulkMode, setBulkMode] = useState(false);
  const [onlyAvailableReal, setOnlyAvailableReal] = useState(false);
  const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));
  const [pageSize] = useState(24);

  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  // Liquidation dialog state — opens from a KPI card or the filter bar.
  const [liqOpen, setLiqOpen] = useState(false);
  const [liqScope, setLiqScope] = useState("dead");

  const openLiq = (scope) => {
    setLiqScope(scope);
    setLiqOpen(true);
  };

  // Default-scope mapping for the inline "Export" button in the filter bar.
  const filterBarScope = view === "old" ? "old" : view === "no_sales" ? "no_sales" : "dead";
  const showFilterBarExport = ["dead", "no_sales", "old"].includes(view);

  // Sync URL.
  useEffect(() => {
    const params = new URLSearchParams();
    if (view !== "all") params.set("view", view);
    if (search) params.set("search", search);
    if (vendor) params.set("vendor", vendor);
    if (productType) params.set("type", productType);
    if (sortBy !== "capital_tied_up") params.set("sort", sortBy);
    if (sortDir !== "desc") params.set("dir", sortDir);
    if (page !== 1) params.set("page", String(page));
    setSp(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, search, vendor, productType, sortBy, sortDir, page]);

  // Default sort per view.
  useEffect(() => {
    if (view === "old") setSortBy("days_in_stock");
    else if (view === "dead") setSortBy("capital_tied_up");
    else if (view === "no_sales") setSortBy("days_since_last_transaction");
    else setSortBy("capital_tied_up");
    setSortDir("desc");
    setPage(1);
  }, [view]);

  const fetchKpis = async () => {
    try {
      const r = await catalog.kpis();
      setKpis(r.data);
    } catch {
      /* ignore */
    }
  };

  const fetchPage = async () => {
    setLoading(true);
    try {
      const params = {
        view,
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        search: search || undefined,
        exclude_as_is: excludeAsIs || undefined,
        only_available_real: onlyAvailableReal || undefined,
      };
      if (hasPhoto !== null) params.has_photo = hasPhoto;
      if (vendor) params.vendor = vendor;
      if (productType) params.product_type = productType;
      const r = await catalog.products(params);
      setData(r.data);
    } catch {
      toast.error(t("common.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, page, sortBy, sortDir, search, vendor, productType, excludeAsIs, hasPhoto, onlyAvailableReal]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const r = await catalog.rebuild();
      toast.success(
        t("catalog.rebuildDone", {
          n: r.data?.total || 0,
          s: r.data?.elapsed_seconds || 0,
        })
      );
      await Promise.all([fetchKpis(), fetchPage()]);
    } catch {
      toast.error(t("common.errorSaving"));
    } finally {
      setRebuilding(false);
    }
  };

  const totalPages = data?.pages || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Package className="h-6 w-6 text-primary" />
            {t("catalog.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("catalog.subtitle")}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <RouterLink to="/inventory" className="inline-flex">
              <Button variant="outline" size="sm" data-testid="catalog-to-inventory-link">
                <Boxes className="mr-1.5 h-3.5 w-3.5" />
                {t("catalog.viewInventory", { defaultValue: "Ver Inventario" })}
              </Button>
            </RouterLink>
            <Button
              onClick={handleRebuild}
              disabled={rebuilding}
              size="sm"
              data-testid="catalog-rebuild"
            >
              <RefreshCw
                className={`mr-1.5 h-3.5 w-3.5 ${rebuilding ? "animate-spin" : ""}`}
              />
              {t("catalog.rebuild")}
            </Button>
          </div>
        )}
        {!canManage && (
          <RouterLink to="/inventory" className="inline-flex">
            <Button variant="outline" size="sm" data-testid="catalog-to-inventory-link">
              <Boxes className="mr-1.5 h-3.5 w-3.5" />
              {t("catalog.viewInventory", { defaultValue: "Ver Inventario" })}
            </Button>
          </RouterLink>
        )}
      </div>

      {/* Phase 5.7 — Bulk-photo CTA banner (only when filtering "missing photo" and not in bulk mode). */}
      {hasPhoto === false && !bulkMode && canManage && kpis && (kpis.without_photo_count > 0) && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3"
          data-testid="bulk-photo-cta"
        >
          <span className="text-2xl">📸</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">
              {kpis.without_photo_count.toLocaleString("es-MX")} productos sin foto
            </div>
            <div className="text-[11px] text-muted-foreground">
              Activa el modo carga rápida — arrastra varias fotos a la vez, las auto-asignamos por SKU.
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setBulkMode(true)}
            data-testid="bulk-photo-cta-btn"
            className="bg-amber-500 text-white hover:bg-amber-600"
          >
            Activar modo carga rápida
          </Button>
        </div>
      )}

      {/* Phase 5.7 — Bulk mode replaces the rest of the page until exited. */}
      {bulkMode && (
        <BulkPhotoUploadMode onExit={() => setBulkMode(false)} />
      )}
      {!bulkMode && (
        <div className="contents" data-testid="catalog-normal-view">
      {/* KPI strip */}
      {kpis && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KpiCard
            label={t("catalog.kpis.totalSkus")}
            value={kpis.total_skus.toLocaleString()}
            sub={t("catalog.kpis.inStockSub", { n: kpis.in_stock_skus.toLocaleString() })}
            Icon={Package}
            tone="default"
            testId="catalog-kpi-total"
          />
          <KpiCard
            label={t("catalog.kpis.totalCapital")}
            value={fmtMoney(kpis.total_capital_tied_up)}
            sub={t("catalog.kpis.realCostSub")}
            Icon={TrendingDown}
            tone="primary"
            testId="catalog-kpi-capital"
          />
          <KpiCard
            label={t("catalog.kpis.deadCapital")}
            value={fmtMoney(kpis.dead_capital)}
            sub={`${kpis.dead_count.toLocaleString()} ${t("catalog.kpis.skus")}`}
            Icon={Skull}
            tone="danger"
            testId="catalog-kpi-dead"
            ctaLabel={t("liquidation.cta")}
            onCta={() => openLiq("dead")}
          />
          <KpiCard
            label={t("catalog.kpis.noSalesCapital")}
            value={fmtMoney(kpis.no_sales_capital)}
            sub={`${kpis.no_sales_count.toLocaleString()} ${t("catalog.kpis.skus")}`}
            Icon={AlertTriangle}
            tone="warning"
            testId="catalog-kpi-no-sales"
            ctaLabel={t("liquidation.cta")}
            onCta={() => openLiq("no_sales")}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList
          className="h-auto w-full justify-start gap-1 bg-transparent p-0"
          data-testid="catalog-tabs"
        >
          {VIEWS.map((v) => {
            const Icon = VIEW_ICONS[v];
            return (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs data-[state=active]:border-primary/60 data-[state=active]:bg-primary/10"
                data-testid={`catalog-tab-${v}`}
              >
                <Icon className="mr-1.5 h-3 w-3" />
                {t(`catalog.views.${v}`)}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <CollapsibleFiltersPanel
        title={t("filters.title", { defaultValue: "Filtros" })}
        activeCount={
          (search ? 1 : 0) +
          (vendor ? 1 : 0) +
          (productType ? 1 : 0) +
          (excludeAsIs ? 1 : 0) +
          (hasPhoto !== null ? 1 : 0) +
          (onlyAvailableReal ? 1 : 0)
        }
      >
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            <div className="col-span-2 sm:col-span-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder={t("catalog.filters.searchPlaceholder")}
                  className="h-8 pl-7 text-xs"
                  data-testid="catalog-search"
                />
              </div>
            </div>
            <Select
              value={vendor || "_all"}
              onValueChange={(v) => {
                setVendor(v === "_all" ? "" : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="catalog-vendor">
                <SelectValue placeholder={t("catalog.filters.vendor")} />
              </SelectTrigger>
              <SelectContent className="max-h-[260px]">
                <SelectItem value="_all">{t("catalog.filters.allVendors")}</SelectItem>
                {(kpis?.facets?.vendors || []).map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={productType || "_all"}
              onValueChange={(v) => {
                setProductType(v === "_all" ? "" : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="catalog-type">
                <SelectValue placeholder={t("catalog.filters.productType")} />
              </SelectTrigger>
              <SelectContent className="max-h-[260px]">
                <SelectItem value="_all">{t("catalog.filters.allTypes")}</SelectItem>
                {(kpis?.facets?.product_types || []).map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs" data-testid="catalog-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`catalog.sort.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortDir} onValueChange={setSortDir}>
              <SelectTrigger className="h-8 text-xs" data-testid="catalog-dir">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">↓ {t("common.desc")}</SelectItem>
                <SelectItem value="asc">↑ {t("common.asc")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={excludeAsIs}
                onChange={(e) => {
                  setExcludeAsIs(e.target.checked);
                  setPage(1);
                }}
                data-testid="catalog-exclude-asis"
              />
              {t("catalog.filters.excludeAsIs")}
            </label>
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={onlyAvailableReal}
                onChange={(e) => {
                  setOnlyAvailableReal(e.target.checked);
                  setPage(1);
                }}
                data-testid="catalog-only-available-real"
              />
              Stock real disponible &gt; 0
            </label>
            <div
              className="flex items-center gap-1.5"
              data-testid="catalog-photo-filter"
            >
              <span>Fotos:</span>
              <button
                type="button"
                onClick={() => {
                  setHasPhoto(null);
                  setPage(1);
                }}
                className={cn(
                  "rounded px-1.5 py-0.5",
                  hasPhoto === null ? "bg-primary/15 text-primary" : "hover:bg-muted"
                )}
                data-testid="catalog-photo-any"
              >
                cualquiera
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasPhoto(true);
                  setPage(1);
                }}
                className={cn(
                  "rounded px-1.5 py-0.5",
                  hasPhoto === true ? "bg-primary/15 text-primary" : "hover:bg-muted"
                )}
                data-testid="catalog-photo-yes"
              >
                con foto
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasPhoto(false);
                  setPage(1);
                }}
                className={cn(
                  "rounded px-1.5 py-0.5",
                  hasPhoto === false ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "hover:bg-muted"
                )}
                data-testid="catalog-photo-no"
              >
                sin foto
              </button>
            </div>
            {data?.total !== undefined && (
              <span>
                <Filter className="mr-1 inline h-3 w-3" />
                {t("catalog.filters.matching", { n: data.total.toLocaleString() })}
              </span>
            )}
            {showFilterBarExport && (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto h-7 text-[11px]"
                onClick={() => openLiq(filterBarScope)}
                data-testid="catalog-filter-export"
              >
                <Download className="mr-1.5 h-3 w-3" />
                {t("liquidation.shortcut")}
              </Button>
            )}
          </div>
        </div>
      </CollapsibleFiltersPanel>

      {/* Grid */}
      {loading ? (
        <div className="rounded-lg border border-border p-12 text-center text-sm text-muted-foreground">
          {t("common.loading")}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          {t("catalog.empty")}
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="catalog-grid"
        >
          {(data?.items || []).map((p) => (
            <ProductCard key={p.sku} product={p} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs"
          data-testid="catalog-pagination"
        >
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="h-7"
          >
            <ChevronLeft className="mr-1 h-3 w-3" />
            {t("common.previous")}
          </Button>
          <span>
            {t("common.page_of", { page, total: totalPages })} —{" "}
            {data.total.toLocaleString()} SKUs
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="h-7"
          >
            {t("common.next")}
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Liquidation dialog */}
      <LiquidationDialog
        open={liqOpen}
        onOpenChange={setLiqOpen}
        initialScope={liqScope}
      />
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, Icon, tone = "default", testId, ctaLabel, onCta }) {
  const tones = {
    default: "border-border bg-card",
    primary: "border-primary/40 bg-primary/5",
    warning: "border-amber-500/40 bg-amber-500/5",
    danger: "border-red-500/40 bg-red-500/5",
  };
  const iconTones = {
    default: "text-muted-foreground",
    primary: "text-primary",
    warning: "text-amber-500",
    danger: "text-red-500",
  };
  return (
    <div
      className={`rounded-lg border p-3 ${tones[tone]}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <Icon className={`h-3.5 w-3.5 ${iconTones[tone]}`} />
      </div>
      <div className="mt-1 text-xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      {ctaLabel && (
        <button
          type="button"
          onClick={onCta}
          data-testid={`${testId}-cta`}
          className="mt-2 inline-flex items-center gap-1 rounded-md border border-current/30 bg-background/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide opacity-90 transition hover:opacity-100"
        >
          <Download className="h-3 w-3" />
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
